import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, BookOpen, GraduationCap, Mic, Gamepad2, Users, Award, Play, ArrowRight, Linkedin } from 'lucide-react';

const Home = () => {
  const stats = [
    { number: '30,000+', label: 'Students Taught', icon: Users },
    { number: '200,000+', label: 'Investors Helped', icon: TrendingUp },
    { number: '18+', label: 'Udemy Courses', icon: BookOpen },
    { number: 'Harvard & MIT', label: 'Certified', icon: Award },
  ];

  const featuredSections = [
    {
      title: 'Wealthy Canadian',
      subtitle: 'Best Selling Book',
      description: 'A hilarious and heartfelt journey to financial freedom, designed for every Canadian. Learn investing strategies, TFSA optimization, RRSP planning, and wealth building.',
      link: '/investors',
      linkText: 'Learn More',
      image: 'https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/tx8gw0to_Screen%20Shot%202026-02-09%20at%208.22.50%20AM.png',
      accent: 'amber',
    },
    {
      title: 'CIRO Exam Prep',
      subtitle: 'Pass Your CIRE & RSE Exams',
      description: 'Comprehensive CIRO certification preparation with 700+ pages study guides, video courses, mock exams, and question banks for Canadian securities licensing.',
      link: '/ciro',
      linkText: 'Start Prep',
      image: 'https://images.unsplash.com/photo-1758270704262-ecc82b23dc37?w=600&q=80',
      accent: 'blue',
    },
    {
      title: 'IFC & CSC Courses',
      subtitle: 'Canadian Securities Exam Prep',
      description: 'Investment Funds in Canada (IFC) and Canadian Securities Course (CSC) exam preparation. Pass your securities licensing exams with confidence.',
      link: '/ifc-csc',
      linkText: 'Enroll Now',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80',
      accent: 'blue',
    },
  ];

  // Structured data for the home page
  const homePageSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Harry Vadalkar Financial Education",
    "description": "Expert Canadian investing courses, CSC exam prep, IFC certification, CIRO exam preparation. Learn stock market investing, mutual funds, ETFs, and wealth building strategies.",
    "url": "https://harryvadalkar.ca",
    "founder": {
      "@type": "Person",
      "name": "Harry Vadalkar"
    },
    "areaServed": "Canada",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Canadian Investing Courses",
      "itemListElement": [
        {
          "@type": "Course",
          "name": "Canadian Securities Course (CSC) Prep",
          "description": "Complete CSC exam preparation course"
        },
        {
          "@type": "Course", 
          "name": "Investment Funds in Canada (IFC) Prep",
          "description": "IFC exam preparation with practice questions"
        },
        {
          "@type": "Course",
          "name": "CIRO Exam Preparation",
          "description": "CIRE and RSE exam prep for securities licensing"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>Harry Vadalkar | Canadian Investing Courses | CSC & IFC Exam Prep</title>
        <meta name="description" content="Harry Vadalkar — CFA, educator, and author of Wealthy Canadian. Expert courses on investing, CSC, IFC, and CIRO exam prep. 30,000+ students. TFSA, RRSP, ETFs, mutual funds, and more." />
        <meta name="keywords" content="Canadian investing courses, CSC exam prep, Canadian Securities Course, CSC exam, IFC exam, IFC exam prep, Investment Funds in Canada, CIRO certification, CIRO exam prep, CIRE exam, RSE exam, investing in Canada, stock market Canada, mutual funds Canada, ETF investing Canada, TFSA strategies, RRSP planning, financial education Canada, Harry Vadalkar, Wealthy Canadian book" />
        <link rel="canonical" href="https://harryvadalkar.ca" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca" />
        <meta property="og:title" content="Harry Vadalkar | Canadian Investing Courses | CSC & IFC Exam Prep" />
        <meta property="og:description" content="Harry Vadalkar — CFA, educator, and author of Wealthy Canadian. Expert courses on investing, CSC, IFC, and CIRO exam prep. 30,000+ students." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg" />
        <meta property="og:site_name" content="Harry Vadalkar" />
        <meta property="og:locale" content="en_CA" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harry Vadalkar | Canadian Investing Courses" />
        <meta name="twitter:description" content="CFA, educator, author of Wealthy Canadian. Expert courses on CSC, IFC, CIRO exam prep. 30,000+ students." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg" />
        <script type="application/ld+json">
          {JSON.stringify(homePageSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1763696878397-bda6c3003b18?w=1920&q=80"
            alt="Canadian Mountains"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/90 to-[#020617]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <p className="text-amber-400 font-accent text-lg tracking-widest uppercase mb-4">
                Financial Educator & Author
              </p>
              <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
                Your Guide to <span className="text-amber-400">Financial Freedom</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 max-w-lg">
                "I believe education should do more than inform—it should inspire, disrupt, and prepare us to lead in a world transformed by technology."
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.amazon.ca/Wealthy-Canadian-hilarious-heartfelt-journey-ebook/dp/B0FDDBRKV7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-book"
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30 hover-glow-gold"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Get the Book
                </a>
                <Link
                  to="/courses"
                  data-testid="hero-cta-courses"
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-blue-500/30"
                >
                  <GraduationCap className="w-5 h-5 mr-2" />
                  View Courses
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 animate-fade-in-up animation-delay-300">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="glass rounded-xl p-6 hover:border-blue-500/50 transition-all group"
                >
                  <stat.icon className="w-8 h-8 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-3xl font-bold text-white mb-1">{stat.number}</p>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-32 bg-slate-900/30" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_e36899ae-c2fa-440f-bf36-c9469e111964/artifacts/f8pxlvyc_HarryVadalkar-Website-4.jpg"
                  alt="Harry Vadalkar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-amber-500 text-black p-6 rounded-xl shadow-xl">
                <p className="font-heading text-4xl font-bold">30K+</p>
                <p className="font-medium">Students</p>
              </div>
            </div>

            <div>
              <p className="text-blue-400 font-accent text-lg tracking-widest uppercase mb-4">About Me</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Making Boring <span className="text-amber-400">Investing Beautiful</span>
              </h2>
              <div className="space-y-4 text-slate-300 text-lg">
                <p>
                  By day, I wield whiteboard markers at SAIT and Mount Royal University. Separately, I help students crush their CIRO and RSE exams.
                </p>
                <p>
                  I'm a retired fintech founder, corporate trainer, and proud collector of credentials—Harvard and MIT certified with CFA, CSC, MBA, and more.
                </p>
                <p>
                  My journey from ramen-fueled investor to early crypto adopter and real estate enthusiast has empowered over 30,000 students and more than 200,000 investors.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                {['CFA', 'CSC', 'MBA', 'Harvard', 'MIT'].map((badge) => (
                  <span
                    key={badge}
                    className="px-4 py-2 bg-slate-800 text-slate-300 rounded-full text-sm font-medium"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <a
                href="https://www.linkedin.com/in/herambvadalkar/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin"
                className="inline-flex items-center gap-2 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-blue-500/30"
              >
                <Linkedin className="w-5 h-5" />
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* IFC Course Promotion */}
      <section className="py-16 md:py-24 relative overflow-hidden" data-testid="ifc-promo-section">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 via-slate-900/50 to-amber-900/20" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/90 border border-blue-500/30 rounded-3xl p-8 md:p-12 shadow-2xl shadow-blue-500/10">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Content Side */}
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full mb-6">
                  <GraduationCap className="w-4 h-4" />
                  <span className="font-semibold text-sm">BESTSELLING COURSE</span>
                </div>
                
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  IFC Exam Prep <span className="text-amber-400">Masterclass</span>
                </h2>
                
                <p className="text-slate-300 text-lg mb-6">
                  The most comprehensive Investment Funds in Canada (IFC) exam preparation course. 
                  Pass your certification with confidence using our proven system.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">18 Chapters</p>
                      <p className="text-slate-400 text-sm">Full Curriculum</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Award className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">6 Mock Exams</p>
                      <p className="text-slate-400 text-sm">600+ Questions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Play className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Video Lessons</p>
                      <p className="text-slate-400 text-sm">HD Quality</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">5,000+</p>
                      <p className="text-slate-400 text-sm">Students Enrolled</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href="https://www.paypal.com/ncp/payment/TP4VL7H55HWSL"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ifc-promo-buy-btn"
                    className="inline-flex items-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-amber-500/30 hover:scale-105 transform"
                  >
                    <GraduationCap className="w-5 h-5 mr-2" />
                    Buy Now - $99.99 CAD
                  </a>
                  <Link
                    to="/ifc"
                    className="inline-flex items-center text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                  >
                    View Course Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>

              {/* Visual Side */}
              <div className="relative">
                <div className="relative bg-gradient-to-br from-blue-600/20 to-amber-500/20 rounded-2xl p-6 border border-slate-700">
                  <div className="absolute -top-4 -right-4 bg-amber-500 text-black font-bold px-4 py-2 rounded-full text-sm shadow-lg">
                    LIFETIME ACCESS
                  </div>
                  <div className="text-center py-8">
                    <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">One-Time Payment</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl md:text-6xl font-bold text-white">$99</span>
                      <span className="text-2xl text-slate-400">.99</span>
                      <span className="text-slate-400 ml-2">CAD</span>
                    </div>
                    <p className="text-green-400 text-sm mt-2">✓ No recurring fees</p>
                    <div className="mt-6 space-y-3 text-left">
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">✓</span>
                        <span>All 18 chapters with interactive content</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">✓</span>
                        <span>6 full mock exams with detailed solutions</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">✓</span>
                        <span>600+ practice questions</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">✓</span>
                        <span>Video tutorials & podcasts</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-300">
                        <span className="text-green-400">✓</span>
                        <span>Unlimited lifetime updates</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Sections */}
      <section className="py-20 md:py-32" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 font-accent text-lg tracking-widest uppercase mb-4">Featured</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              Explore My <span className="text-amber-400">Resources</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredSections.map((section, index) => (
              <div
                key={index}
                className="group bg-slate-900/50 border border-slate-800 hover:border-blue-500/50 rounded-2xl overflow-hidden transition-all duration-300"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className={`text-${section.accent === 'amber' ? 'amber-400' : 'blue-400'} text-sm font-medium mb-2`}>
                    {section.subtitle}
                  </p>
                  <h3 className="font-heading text-2xl font-bold text-white mb-3">{section.title}</h3>
                  <p className="text-slate-400 mb-6">{section.description}</p>
                  <Link
                    to={section.link}
                    className={`inline-flex items-center font-semibold ${
                      section.accent === 'amber' ? 'text-amber-400 hover:text-amber-300' : 'text-blue-400 hover:text-blue-300'
                    } transition-colors`}
                  >
                    {section.linkText}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Channel Promotion */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-slate-900/30" data-testid="youtube-promo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-full mb-6">
                <Play className="w-4 h-4" />
                <span className="font-medium">YouTube Channel</span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Finance in <span className="text-red-500">Canada</span>
              </h2>
              <p className="text-slate-300 text-lg mb-6">
                Join 11,000+ subscribers learning to invest smarter. Get exclusive access to masterclasses, exam prep content, and weekly live sessions with membership.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/youtube"
                  data-testid="youtube-promo-link"
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-full transition-all"
                >
                  <Play className="w-5 h-5 mr-2" />
                  View Memberships
                </Link>
                <a
                  href="https://www.youtube.com/@FinanceinCanada"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white font-semibold px-6 py-3 rounded-full transition-all"
                >
                  Subscribe Free
                </a>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { level: '1', name: 'Investing in Canada', desc: 'TFSA, RRSP & Portfolio Building', color: 'blue' },
                { level: '2', name: 'Industry Exam Prep', desc: 'IFC, CSC & CIRE Tutorials', color: 'amber' },
                { level: '3', name: 'Advanced Exams', desc: 'RSE & Advanced CIRE', color: 'purple' },
                { level: '4', name: 'Ultimate Mentorship', desc: 'CFA, FRM & Live Sessions', color: 'amber' },
              ].map((tier, index) => (
                <div key={index} className="glass rounded-xl p-4 hover:border-slate-700 transition-all">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    tier.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                    tier.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    Level {tier.level}
                  </span>
                  <h4 className="font-semibold text-white mt-2 text-sm">{tier.name}</h4>
                  <p className="text-slate-400 text-xs mt-1">{tier.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Grid */}
      <section className="py-20 md:py-32" data-testid="quick-links">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Podcasts', icon: Mic, link: '/podcasts', color: 'text-purple-400' },
              { name: 'Games', icon: Gamepad2, link: '/games', color: 'text-green-400' },
              { name: 'Blog', icon: BookOpen, link: '/blog', color: 'text-blue-400' },
              { name: 'YouTube', icon: Play, link: '/youtube', color: 'text-red-400' },
            ].map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="glass rounded-xl p-6 text-center hover:border-slate-700 transition-all group"
              >
                <item.icon className={`w-10 h-10 mx-auto mb-3 ${item.color} group-hover:scale-110 transition-transform`} />
                <p className="text-white font-medium">{item.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;